import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Package, Mail } from 'lucide-react';
import { Button } from './ui/button';
import SmartImage from './SmartImage';
import { useCart } from '../context/CartContext';
import CartAddedModal from './CartAddedModal';
import PreorderModal from './PreorderModal';
import MixedCartWarningModal from './MixedCartWarningModal';
import { isProductEligible, monthlyRateFromNet } from '../utils/paypalEligibility';
import { isInquiryOnly as checkInquiryOnly, inquiryUrl } from '../utils/inquiryRules';

/**
 * ProductCard Component with optional Dual-Buttons for Preorder
 * 
 * @param {Object} product - Product data
 * @param {string} linkPrefix - URL prefix for product detail (e.g., '/bagger', '/radlader')
 * @param {boolean} showButtons - Whether to show Add to Cart / Preorder buttons
 */
const ProductCard = ({ product, linkPrefix = '/produkt', showButtons = true }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [showCartModal, setShowCartModal] = useState(false);
  const [showPreorderModal, setShowPreorderModal] = useState(false);
  const [showMixedCartWarning, setShowMixedCartWarning] = useState(false);
  const [mixedCartMessage, setMixedCartMessage] = useState('');
  const [addedProduct, setAddedProduct] = useState(null);
  
  const isInStock = (product.stockQuantity ?? 10) > 0;
  const hasPrice = product.pricing?.numericPrice && product.pricing.numericPrice > 0;

  const isAccessoryCard = product.type === 'accessory' || linkPrefix === '/zubehoer';
  const isInquiryOnly = checkInquiryOnly({ slug: product.slug, category: product.category, isAccessory: isAccessoryCard });

  const paypalMonthly = (isInStock && hasPrice)
    ? monthlyRateFromNet(product.pricing.numericPrice)
    : null;
  const paypalEligible = paypalMonthly != null && isProductEligible({
    slug: product.slug,
    category: product.category,
    isAccessory: isAccessoryCard,
    stockQuantity: product.stockQuantity,
    availability: product.availability,
  });
  const paypalMonthlyStr = paypalEligible
    ? paypalMonthly.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : null;
  
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!hasPrice) {
      navigate('/kontakt');
      return;
    }
    
    const result = addToCart(product, 1, false);
    
    if (result.success) {
      setAddedProduct(product);
      setShowCartModal(true);
    } else {
      // Show mixed cart warning modal instead of toast
      setMixedCartMessage(result.error);
      setShowMixedCartWarning(true);
    }
  };
  
  const handlePreorder = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isInquiryOnly) {
      navigate(inquiryUrl(product.name || product.slug));
      return;
    }

    if (!hasPrice) {
      navigate('/kontakt');
      return;
    }
    
    const result = addToCart(product, 1, true);
    
    if (result.success) {
      setAddedProduct(product);
      setShowPreorderModal(true);
    } else {
      // Show mixed cart warning modal instead of toast
      setMixedCartMessage(result.error);
      setShowMixedCartWarning(true);
    }
  };
  
  // Calculate preorder discount price
  const preorderPrice = hasPrice ? product.pricing.numericPrice * 0.90 : 0;
  
  return (
    <>
      <div className="block rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] h-full relative bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08),0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12),0_8px_24px_rgba(0,0,0,0.08)] border border-gray-100">
        {/* PayPal-Ratenzahlung Ribbon (top-left over the image) */}
        {paypalMonthlyStr && (
          <div
            className="absolute top-3 left-3 z-10 max-w-[55%]"
            data-testid={`paypal-ribbon-${product.id}`}
          >
            <div className="bg-blue-700 text-white text-[11px] sm:text-xs font-semibold px-2.5 py-1.5 rounded-md shadow-md leading-tight">
              <div>PayPal-Ratenzahlung</div>
              <div className="font-bold">ab ca. {paypalMonthlyStr} €/Monat</div>
            </div>
          </div>
        )}

        {/* Availability Badge */}
        <div className="absolute top-3 right-3 z-10">
          {!isInStock ? (
            <span className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] sm:text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              {isInquiryOnly ? 'Auf Anfrage' : 'Vorbestellen'}
            </span>
          ) : (product.stockQuantity ?? 10) <= 2 ? (
            <span className="bg-orange-50 text-orange-700 border border-orange-200 text-[10px] sm:text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
              Nur noch {product.stockQuantity} Stk.
            </span>
          ) : (
            <span className="bg-green-50 text-green-700 border border-green-200 text-[10px] sm:text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1.5 shadow-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              Sofort lieferbar
            </span>
          )}
        </div>
        
        {/* Clickable Product Link Area */}
        <Link 
          to={`${linkPrefix}/${product.slug}`}
          className="block"
          data-testid={`product-card-link-${product.id}`}
        >
          {/* Image Container */}
          <div className="bg-white mt-10 mx-3 pt-2 flex items-end justify-center pb-1 relative" style={{minHeight: '160px'}}>
            <SmartImage
              src={product.image}
              alt={product.name}
              className="max-w-full max-h-36 sm:max-h-44 md:max-h-52 object-contain"
              data-testid={`product-image-${product.id}`}
            />
          </div>
          
          <div className="px-3 pb-2 sm:px-4 bg-white flex flex-col -mt-px">
            <h3 className="text-sm sm:text-lg font-bold text-gray-900 text-center leading-tight">
              {product.name}
            </h3>
            {product.deliveryTime && (
              <p className="text-xs sm:text-sm text-gray-500 text-center mt-1">
                {!isInStock ? 'Ca. 60 Tage Lieferzeit' : product.deliveryTime}
              </p>
            )}
            {!product.deliveryTime && !isInStock && (
              <p className="text-xs sm:text-sm text-gray-500 text-center mt-1">
                Ca. 60 Tage Lieferzeit
              </p>
            )}
            <div className="mt-auto pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-0.5">Verkaufspreis</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600 text-center">{product.pricing?.buy || 'Auf Anfrage'}</p>
            </div>
          </div>
        </Link>
        
        {/* Action Buttons */}
        {showButtons && hasPrice && (
          <div className="px-3 pb-3 sm:px-4 sm:pb-4 bg-white">
            {isInStock ? (
              /* In Stock: Show both buttons */
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleAddToCart}
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  In den Warenkorb
                </Button>
                <Button
                  onClick={handlePreorder}
                  variant="outline"
                  className={`w-full text-xs sm:text-sm py-2 ${
                    isInquiryOnly
                      ? 'border-blue-400 text-blue-700 hover:bg-blue-50'
                      : 'border-amber-400 text-amber-700 hover:bg-amber-50'
                  }`}
                  data-testid={isInquiryOnly ? `inquiry-${product.id}` : `preorder-${product.id}`}
                >
                  {isInquiryOnly ? (
                    <>
                      <Mail className="w-4 h-4 mr-1" />
                      Anfrage stellen
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4 mr-1" />
                      Vorbestellen (-10%)
                    </>
                  )}
                </Button>
              </div>
            ) : (
              /* Out of Stock: Only one action button */
              <Button
                onClick={handlePreorder}
                className="w-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-5 h-auto min-h-[76px]"
                data-testid={isInquiryOnly ? `inquiry-only-${product.id}` : `preorder-only-${product.id}`}
              >
                {isInquiryOnly ? (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Anfrage stellen
                  </>
                ) : (
                  <>
                    <Package className="w-5 h-5 mr-2" />
                    Vorbestellen (-10%)
                  </>
                )}
              </Button>
            )}
          </div>
        )}
        
        {/* No Price: Show "Request Quote" link */}
        {showButtons && !hasPrice && (
          <div className="px-3 pb-3 sm:px-4 sm:pb-4 bg-white">
            <Link to="/kontakt">
              <Button
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50 text-xs sm:text-sm py-2"
              >
                Angebot anfordern
              </Button>
            </Link>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showCartModal && addedProduct && (
        <CartAddedModal
          isOpen={showCartModal}
          onClose={() => setShowCartModal(false)}
          product={addedProduct}
        />
      )}
      
      {showPreorderModal && addedProduct && (
        <PreorderModal
          isOpen={showPreorderModal}
          onClose={() => setShowPreorderModal(false)}
          product={addedProduct}
        />
      )}
      
      {/* Mixed Cart Warning Modal */}
      <MixedCartWarningModal
        isOpen={showMixedCartWarning}
        onClose={() => setShowMixedCartWarning(false)}
        message={mixedCartMessage}
      />
    </>
  );
};

export default ProductCard;
